import { isEmpty } from 'lib';
import { commaSeperator, getDateTime } from 'utils';
import translation from 'translation';
import jmoment from 'moment-jalaali';
import { getBrandLogoUrl, getRatingObject } from './ecommerce.helpers';

const { t } = translation;

export const adaptBrandHealth = (rawData) => {
  if (!rawData || !rawData.score) {
    return {
      score: null,
      change: null,
    };
  }

  const { score, change } = rawData;

  const changeDirection =
    change?.direction === 'up' ? change?.percent || 0 : -(change?.percent || 0);

  return {
    score: {
      value: score?.value || 0,
      level: score?.level || '',
      label: score?.label_fa || '',
      color: score?.color || '',
    },
    change: {
      percent: change?.percent || 0,
      direction: change?.direction || 'neutral',
      changeDirection,
    },
  };
};

export const adaptBrandDetails = (rawBrand, platform) => {
  if (!rawBrand) {
    return {
      brand: null,
      counts: null,
    };
  }

  return {
    brand: {
      name: rawBrand.brand_title || '',
      logo: getBrandLogoUrl(rawBrand.logo),
      code: rawBrand.code || '',
      primaryColor: null,
      secondaryColor: null,
      slug: rawBrand.code || '',
      platform: platform || '',
    },
    counts: {
      totalProducts: rawBrand.product_count || 0,
      totalCategories: rawBrand.category_count || 0,
    },
  };
};

export const adaptProductTrend = (rawData) => {
  if (!rawData || !rawData.series) {
    return {
      summary: null,
      chartData: [],
      legends: [],
    };
  }

  const { summary, series } = rawData;

  const activeProductsSeries = series.find((s) => s.key === 'active_products');
  const existingProductsSeries = series.find(
    (s) => s.key === 'existing_products'
  );

  const activePoints = activeProductsSeries?.points || [];
  const existingPoints = existingProductsSeries?.points || [];

  const activeColor = '#FAA13E';
  const existingColor = '#2D66F7';

  const activeLabel = t('ecommerce:productTrend.activeProducts');
  const existingLabel = t('ecommerce:productTrend.existingProducts');

  const flatChartData = [];

  activePoints.forEach((point, index) => {
    const time = point.timestamp;
    const existingPoint = existingPoints[index];

    flatChartData.push({
      id: 0,
      time,
      count: point.value || 0,
      color: activeColor,
      type: 'line',
      q: activeLabel,
    });

    if (existingPoint) {
      flatChartData.push({
        id: 1,
        time,
        count: existingPoint.value || 0,
        color: existingColor,
        type: 'line',
        q: existingLabel,
      });
    }
  });

  const legends = [
    {
      id: 0,
      title: activeLabel,
      name: activeLabel,
      key: 'active_products',
      color: activeColor,
      type: 'line',
    },
    {
      id: 1,
      title: existingLabel,
      name: existingLabel,
      key: 'existing_products',
      color: existingColor,
      type: 'line',
    },
  ];

  const summaryData = {
    totalProducts: {
      value: commaSeperator(summary?.total_products?.value || 0),
      changePercent: summary?.total_products?.change_percent || 0,
    },
    totalActiveProducts: {
      value: commaSeperator(summary?.total_active_products?.value || 0),
      changePercent: summary?.total_active_products?.change_percent || 0,
    },
    totalExistingProducts: {
      value: commaSeperator(summary?.total_existing_products?.value || 0),
      changePercent: summary?.total_existing_products?.change_percent || 0,
    },
  };

  return {
    summary: summaryData,
    chartData: flatChartData,
    legends,
  };
};

export const adaptUserSatisfaction = (rawData) => {
  const chartKeys = {
    barChartKeys: [
      { key: 'negative', fill: '#F6B5B6', stackId: '1' },
      { key: 'neutral', fill: '#CCD5DC', stackId: '1' },
      { key: 'positive', fill: '#A0F1AF', stackId: '1' },
    ],
    lineChartKeys: [{ key: 'nps', stroke: '#2D66F7' }],
  };

  const adaptedChartData = rawData?.charts?.points?.map((item) => {
    return {
      name: getDateTime(item.timestamp).date,
      ...item,
    };
  });

  const legends = {
    nps: {
      color: '#2D66F7',
      title: t('ecommerce:userSatisfactions.npsLabel'),
      suffix: '%',
    },
    positive: {
      color: '#A0F1AF',
      title: t('ecommerce:userSatisfactions.positiveLabel'),
    },
    neutral: {
      color: '#CCD5DC',
      title: t('ecommerce:userSatisfactions.neutralLabel'),
    },
    negative: {
      color: '#F6B5B6',
      title: t('ecommerce:userSatisfactions.negativeLabel'),
    },
  };

  const summary = {
    totalReviews: {
      value: commaSeperator(rawData?.summary?.total_reviews?.value || 0),
      changePercent: Math.round(
        rawData?.summary?.total_reviews?.change_percent || 0
      ),
    },
    nps: {
      value: commaSeperator(Math.round(rawData?.summary?.nps?.value || 0)),
      changePercent: Math.round(rawData?.summary?.nps?.change_percent || 0),
    },
    positiveReviews: {
      value: commaSeperator(rawData?.summary?.positive_reviews?.value || 0),
      changePercent: Math.round(
        rawData?.summary?.positive_reviews?.change_percent || 0
      ),
    },
    negativeReviews: {
      value: commaSeperator(rawData?.summary?.negative_reviews?.value || 0),
      changePercent: Math.round(
        rawData?.summary?.negative_reviews?.change_percent || 0
      ),
    },
    neutralReviews: {
      value: commaSeperator(rawData?.summary?.neutral_reviews?.value || 0),
      changePercent: Math.round(
        rawData?.summary?.neutral_reviews?.change_percent || 0
      ),
    },
  };

  return {
    data: adaptedChartData,
    chartKeys,
    legends,
    summary,
  };
};

export const adaptRiskMonitor = (rawData) => {
  const { mode, item } = rawData || {};

  if (!item) {
    return {
      mode: mode || null,
      entity: null,
      risk: null,
      kpis: null,
      topics: [],
      chartData: [],
    };
  }

  const topics = (item.topics || []).map((topic) => ({
    title: topic?.title || '',
    percent: topic?.percent || 0,
    sentiment: topic?.sentiment || 'positive',
  }));

  return {
    mode: mode || null,
    entity: item.entity
      ? {
          id: item.entity.id || null,
          type: item.entity.type || '',
          title: item.entity.title || '',
          imageUrl: item.entity.image_url || '',
          categoryId: item.entity.category_id || null,
        }
      : null,
    risk: item.risk
      ? {
          score: item.risk.score || 0,
          label: item.risk.label_fa || '',
          level: item.risk.level || '',
          color: item.risk.color || '',
        }
      : null,
    kpis: item.kpis
      ? {
          avgRating: item.kpis.avg_rating || 0,
          avgRatingChangePercent: item.kpis.avg_rating_change_percent || 0,
          negativeBuyersCount: item.kpis.negative_buyers_count || 0,
          negativeBuyersPercent: item.kpis.negative_buyers_percent || 0,
          totalVerifiedReviews: item.kpis.total_verified_reviews || 0,
          rating: getRatingObject(item.kpis.rating),
        }
      : null,
    topics,
    chartData: topics.map((topic) => ({
      name: topic.title,
      value: topic.percent,
    })),
  };
};

export const adaptReviewShare = (rawData) => {
  const myBrandColor = '#2D66F7';
  const othersColor = '#F8BC77';

  const adaptedChartData = rawData?.pie?.map((item) => {
    return {
      item: item.label_fa,
      count: `${commaSeperator(item.value)} ${t('ecommerce:review')}`,
      percent: item.percent,
      color: item.key === 'brand' ? myBrandColor : othersColor,
    };
  });

  return {
    data: adaptedChartData,
  };
};

export const adaptProductPerformance = (rawData) => {
  const { mode, item } = rawData || {};

  if (!item) {
    return {
      mode: mode || null,
      product: null,
      kpis: null,
      topics: [],
    };
  }

  const topics = (item.topics || []).map((topic) => ({
    key: topic?.key || '',
    title: topic?.title_fa || '',
    percent: topic?.percent || 0,
    sentiment: topic?.sentiment || '',
  }));

  return {
    mode: mode || null,
    product: item.product
      ? {
          skuId: item.product.sku_id || null,
          title: item.product.title || '',
          imageUrl: item.product.image_url || '',
          categoryId: item.product.category_id || null,
        }
      : null,
    kpis: item.kpis
      ? {
          avgRating: item.kpis.avg_rating || 0,
          avgRatingChangePercent: item.kpis.avg_rating_change_percent || 0,
          reviewsCount: item.kpis.reviews_count || 0,
          verifiedRatersCount: item.kpis.verified_raters_count || 0,
          rating: getRatingObject(item.kpis.rating),
        }
      : null,
    topics,
  };
};

export const adaptLatestReviews = (rawData) => {
  const items = rawData?.items || [];

  return items.map((item) => {
    const review = item.review || {};
    const product = item.product || {};
    const topics = item.topics || [];

    return {
      review: {
        id: review.id || null,
        createdAt: review.created_at || null,
        formattedDate: review.created_at
          ? jmoment(review.created_at).format('jD jMMMM jYYYY')
          : null,
        rating: getRatingObject(review.rating),
        isVerified: review.is_verified || false,
        text: review.text || '',
      },
      product: {
        skuId: product.sku_id || null,
        title: product.title || '',
        imageUrl: product.image_url || null,
        buyerCountHint: product.buyer_count_hint || 0,
        commentsCount: product.comments_count || 0,
        rating: getRatingObject(product.rating),
      },
      topics: topics.map((topic) => ({
        title: topic.title_fa,
        percent: topic.percent,
        sentiment: topic.sentiment,
      })),
    };
  });
};

export const adaptReviewShareByCategory = (rawData) => {
  if (!rawData) {
    return {
      summary: null,
      treemapData: [],
    };
  }

  const items = rawData.items || [];
  const treemapData = items.map((item) => {
    const reviewsCount = item.reviews?.count || 0;

    return {
      title: item.category?.title || '',
      reviews: reviewsCount,
      changePercent: item.reviews?.change_percent || 0,
      reviewsPerProduct: item.kpis?.reviews_per_product || 0,
      size: reviewsCount,
    };
  });

  return {
    summary: rawData.summary || null,
    treemapData,
  };
};

export const adaptDigikalaComment = (rawComment) => {
  const productId = rawComment?.resource_product_id;
  const formattedDate = rawComment?.time
    ? getDateTime(rawComment.time).date
    : null;
  const [productImageUrl] = Array.isArray(rawComment?.resource_images)
    ? rawComment.resource_images
    : [];

  return {
    id: rawComment?.id,
    product: {
      brand: rawComment?.resource_brand_code,
      brandCode: rawComment?.resource_brand_code,
      title: rawComment?.resource_title,
      productId,
      sku: productId != null ? String(productId) : null,
      imageUrl: productImageUrl || null,
    },
    date: formattedDate,
    userRating: rawComment?.rate,
    reviewText: rawComment?.raw_content,
    userName: isEmpty(rawComment?.user_name)
      ? t('ecommerce:reviewsTable.digikalaUser')
      : rawComment.user_name,
    isBuyer: Boolean(rawComment?.is_buyer),
    sellerId: rawComment?.resource_seller_id,
    sellerName: rawComment?.resource_seller_name,
    likeCount: rawComment?.reactions_likes,
    dislikeCount: rawComment?.reactions_dislikes,
  };
};

export const adaptDigikalaComments = (rawComments = []) =>
  rawComments.map(adaptDigikalaComment);

export const adaptDigikalaBrandOptions = (rawBrands = []) =>
  rawBrands
    .filter((item) => !isEmpty(item?.resource_brand_code))
    .map((item) => ({
      id: item.resource_brand_code,
      label: item.resource_brand_code,
      count: item.count,
    }));

/**
 * Normalize assigned ecommerce brand codes from brand-identity API.
 * Accepts string[] or objects with code/name fields.
 */
export const adaptEcommerceBrandCodes = (rawData = []) => {
  const items = Array.isArray(rawData) ? rawData : [];

  return items
    .map((item) => {
      if (typeof item === 'string') {
        return isEmpty(item) ? null : { id: item, label: item };
      }

      const code =
        item?.resource_brand_code || item?.brand_code || item?.code || item?.id;

      if (isEmpty(code)) {
        return null;
      }

      const label = item?.name || item?.title || item?.label || code;

      return {
        id: String(code),
        label: String(label),
      };
    })
    .filter(Boolean);
};

export const mergeBrandFilterOptions = (
  assignedBrands = [],
  competitors = []
) => {
  const seen = new Set();
  const merged = [];

  [...assignedBrands, ...competitors].forEach((option) => {
    if (!option?.id || seen.has(option.id)) {
      return;
    }

    seen.add(option.id);
    merged.push(option);
  });

  return merged;
};

export const adaptDigikalaCategoryOptions = (rawCategories = []) =>
  rawCategories
    .map((item) => {
      const code = item?.resource_category_code;
      const label = item?.resource_category;

      if (isEmpty(code) || isEmpty(label)) {
        return null;
      }

      return {
        id: String(code),
        label: String(label),
        count: item.count,
      };
    })
    .filter(Boolean);

export const adaptDigikalaProductOptions = (rawProducts = []) =>
  rawProducts
    .filter((item) => item?.resource_product_id != null)
    .map((item) => ({
      id: String(item.resource_product_id),
      label: item.resource_title ?? '',
      brandCode: item.resource_brand_code,
      category: item.resource_category,
      count: item.comment_count,
    }));

export const adaptDigikalaSellerOptions = (rawSellers = []) =>
  rawSellers
    .filter((item) => item?.resource_seller_id != null)
    .map((item) => ({
      id: String(item.resource_seller_id),
      label: item.resource_seller_name ?? '',
      count: item.comment_count,
    }));
